import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useTableSort, SortTh, useColumns, ColumnPicker, ExportCsvButton } from "@/components/SortableHeader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { TeamFormDialog } from "@/components/TeamFormDialog";
import { MessagesSquare, Lock, Globe, Eye, EyeOff, Search, LayoutGrid, List, Plus, Pencil, Trash2 } from "lucide-react";

export default function Teams() {
  const { can } = useAuth();
  const isManager = can("teams.manage");
  const isAdmin = can("teams.manage");
  const { t } = useLang();
  const [teams, setTeams] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [visFilter, setVisFilter] = useState("visible");
  const [view, setView] = useState("list");
  const [users, setUsers] = useState([]);
  const [conventions, setConventions] = useState([]);
  const [form, setForm] = useState({ open: false, mode: "create", team: null });
  const [delTeam, setDelTeam] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => api.get("/teams").then((r) => setTeams(r.data));
  useEffect(() => {
    load();
    if (isManager) {
      api.get("/users").then((r) => setUsers(r.data.filter((u) => !u.isShared))).catch(() => {});
      api.get("/config").then((r) => setConventions((r.data.namingConventions || []).filter((c) => (c.appliesTo || []).includes("team")))).catch(() => {});
    }
  }, [isManager]);

  const doDelete = async () => {
    setDeleting(true);
    try { await api.delete(`/teams/${delTeam.id}`); toast.success(t("teams.deleted")); setDelTeam(null); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setDeleting(false); }
  };

  const toggleVis = async (tid, isVisible) => {
    setTeams((ts) => ts.map((x) => x.id === tid ? { ...x, isVisible } : x));
    try { await api.patch(`/teams/${tid}/visibility`, { isVisible }); }
    catch (e) { toast.error(apiErr(e)); setTeams((ts) => ts.map((x) => x.id === tid ? { ...x, isVisible: !isVisible } : x)); }
  };

  const isPublic = (tm) => (tm.visibility || "").toLowerCase() === "public";

  const sort = useTableSort("displayName", "asc", "teams");
  const cols = useColumns("teams");
  const rows = useMemo(() => {
    const r = teams.filter((tm) =>
      (filter === "all" || (filter === "public" ? isPublic(tm) : !isPublic(tm))) &&
      (visFilter === "all" || (visFilter === "visible" ? tm.isVisible !== false : tm.isVisible === false)) &&
      `${tm.displayName} ${tm.description || ""}`.toLowerCase().includes(q.toLowerCase()));
    return sort.sortRows(r, {
      visibility: (tm) => isPublic(tm) ? 1 : 0,
      memberCount: (tm) => tm.memberCount ?? 0,
      isVisible: (tm) => tm.isVisible !== false ? 1 : 0,
    });
  }, [teams, q, filter, visFilter, sort]);
  const csvRows = rows.map((tm) => ({ displayName: tm.displayName, visibility: isPublic(tm) ? t("teams.public") : t("teams.private"), memberCount: tm.memberCount ?? 0 }));
  const csvHeaders = [
    { key: "displayName", label: t("teams.colName") },
    ...(cols.show("visibility") ? [{ key: "visibility", label: t("teams.colVisibility") }] : []),
    { key: "memberCount", label: t("teams.colMembers") },
  ];

  const VisToggle = ({ tm }) => {
    const hidden = tm.isVisible === false;
    return (
      <button onClick={() => toggleVis(tm.id, hidden)} data-testid={`team-vis-${tm.id}`}
        title={hidden ? t("groups.toggleHidden") : t("groups.toggleVisible")}
        className={`flex h-8 w-8 items-center justify-center border transition-colors ${hidden ? "border-zinc-200 bg-zinc-50 text-zinc-300 hover:text-zinc-500" : "border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100"}`}>
        {hidden ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    );
  };

  const VisBadge = ({ tm }) => (
    <span className="inline-flex items-center gap-1.5 border border-zinc-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
      {isPublic(tm) ? <Globe size={12} /> : <Lock size={12} />} {isPublic(tm) ? t("teams.public") : t("teams.private")}
    </span>
  );

  return (
    <>
      <PageHeader title={t("teams.title")} subtitle={`${rows.length} ${t("teams.subtitle")}`}>
        {isManager && (
          <Button onClick={() => setForm({ open: true, mode: "create", team: null })} data-testid="teams-create-button" className="rounded-sm bg-primary hover:bg-[#002480]">
            <Plus size={16} className="mr-2" /> {t("teams.create")}
          </Button>
        )}
      </PageHeader>
      <div className="p-6 md:p-10">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative w-full max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("teams.searchPh")} data-testid="teams-search" className="rounded-sm border-zinc-300 pl-9" />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <ColumnPicker label={t("cols.columns")} cols={cols} columns={[{ key: "visibility", label: t("teams.colVisibility") }]} />
            <ExportCsvButton label={t("cols.export")} filename="teams.csv" headers={csvHeaders} rows={csvRows} />
          </div>
          <div className="flex border border-zinc-200">
            <button onClick={() => setView("list")} data-testid="teams-view-list" className={`p-2 ${view === "list" ? "bg-zinc-900 text-white" : "bg-white text-zinc-500"}`}><List size={16} /></button>
            <button onClick={() => setView("card")} data-testid="teams-view-card" className={`p-2 ${view === "card" ? "bg-zinc-900 text-white" : "bg-white text-zinc-500"}`}><LayoutGrid size={16} /></button>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="border border-zinc-200 bg-white p-10 text-center text-sm text-zinc-400">{t("teams.empty")}</div>
        ) : view === "list" ? (
          <div className="border border-zinc-200 bg-white">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                  <SortTh label={t("teams.colName")} sortKey="displayName" sort={sort} />
                  {cols.show("visibility") && <SortTh label={t("teams.colVisibility")} sortKey="visibility" sort={sort} className="hidden md:table-cell" filter={{ value: filter, onChange: setFilter, allValue: "all", options: [
                    { value: "all", label: t("common.all") },
                    { value: "public", label: t("teams.filterPublic") },
                    { value: "private", label: t("teams.filterPrivate") },
                  ] }} />}
                  <SortTh label={t("teams.colMembers")} sortKey="memberCount" sort={sort} />
                  {isAdmin && <SortTh label={t("groups.colVisible")} sortKey="isVisible" sort={sort} filter={{ value: visFilter, onChange: setVisFilter, allValue: "all", options: [
                    { value: "visible", label: t("groups.visVisible") },
                    { value: "hidden", label: t("groups.visHidden") },
                    { value: "all", label: t("groups.visAll") },
                  ] }} />}
                  {isManager && <th className="px-4 py-3 text-right">{t("teams.colActions")}</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((tm) => (
                  <tr key={tm.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`team-row-${tm.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className="flex h-8 w-8 items-center justify-center bg-primary/10 text-primary"><MessagesSquare size={16} /></span>
                        <div><div className="text-sm font-semibold text-zinc-900">{tm.displayName}</div><div className="text-xs text-zinc-400">{tm.description}</div></div>
                      </div>
                    </td>
                    {cols.show("visibility") && <td className="hidden px-4 py-3 md:table-cell"><VisBadge tm={tm} /></td>}
                    <td className="px-4 py-3 font-display text-lg font-black text-zinc-900">{tm.memberCount ?? 0}</td>
                    {isAdmin && <td className="px-4 py-3"><VisToggle tm={tm} /></td>}
                    {isManager && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setForm({ open: true, mode: "edit", team: tm })} data-testid={`team-edit-${tm.id}`} title={t("teams.edit")} className="flex h-8 w-8 items-center justify-center border border-zinc-200 text-zinc-500 hover:bg-zinc-100 hover:text-primary"><Pencil size={14} /></button>
                          <button onClick={() => setDelTeam(tm)} data-testid={`team-delete-${tm.id}`} title={t("teams.delete")} className="flex h-8 w-8 items-center justify-center border border-zinc-200 text-zinc-500 hover:bg-red-50 hover:text-red-600"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-px bg-zinc-200 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((tm) => (
              <div key={tm.id} className="flex flex-col bg-white p-5" data-testid={`team-card-${tm.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex h-9 w-9 items-center justify-center bg-primary/10 text-primary"><MessagesSquare size={18} /></div>
                  <span className="font-display text-2xl font-black tracking-tighter text-zinc-950">{tm.memberCount ?? 0}</span>
                </div>
                <div className="mt-3 text-sm font-bold text-zinc-900">{tm.displayName}</div>
                <div className="mt-0.5 flex-1 text-xs text-zinc-500">{tm.description}</div>
                <div className="mt-3 flex items-center justify-between">
                  <VisBadge tm={tm} />
                  <div className="flex items-center gap-1">
                    {isManager && <button onClick={() => setForm({ open: true, mode: "edit", team: tm })} data-testid={`team-edit-card-${tm.id}`} className="flex h-8 w-8 items-center justify-center border border-zinc-200 text-zinc-500 hover:text-primary"><Pencil size={14} /></button>}
                    {isManager && <button onClick={() => setDelTeam(tm)} data-testid={`team-delete-card-${tm.id}`} className="flex h-8 w-8 items-center justify-center border border-zinc-200 text-zinc-500 hover:text-red-600"><Trash2 size={14} /></button>}
                    {isAdmin && <VisToggle tm={tm} />}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <TeamFormDialog open={form.open} onOpenChange={(o) => setForm((f) => ({ ...f, open: o }))}
        mode={form.mode} team={form.team} users={users} conventions={conventions} onSaved={load} />

      <Dialog open={!!delTeam} onOpenChange={(o) => !o && setDelTeam(null)}>
        <DialogContent className="rounded-sm" data-testid="team-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">{t("teams.deleteConfirm")}</DialogTitle>
            <DialogDescription className="text-xs">{t("teams.deleteWarn")} <strong>{delTeam?.displayName}</strong></DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTeam(null)} className="rounded-sm">{t("common.cancel")}</Button>
            <Button onClick={doDelete} disabled={deleting} data-testid="team-delete-confirm" className="rounded-sm bg-red-600 hover:bg-red-700">{t("teams.delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
